import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Check } from "lucide-react";
import { toast } from "sonner";

export default function CheckInPanel({ clientId }) {
  const { user } = useAuth();
  const isTeam = user && (user.role === "admin" || user.role === "team" || user.role === "account_manager");
  const [busy, setBusy] = useState(false);
  const [client, setClient] = useState(null);
  const [customAnswers, setCustomAnswers] = useState({}); // pending input
  const [customSubmitted, setCustomSubmitted] = useState({}); // {fieldId: record}

  const load = async () => {
    if (!isTeam) return;
    const [c, cf] = await Promise.all([
      api.get(`/clients/${clientId}`),
      api.get(`/clients/${clientId}/checkins/today/custom`).catch(() => ({ data: {} })),
    ]);
    setClient(c.data);
    setCustomSubmitted(cf.data || {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  if (!isTeam) return null;
  const fields = client?.check_in_fields || [];

  return (
    <aside className="space-y-4" data-testid="checkin-panel">
      <div className="tt-card p-4" data-testid="custom-checkin-section">
        <div className="flex items-center justify-between mb-2.5">
          <span className="t-eyebrow">Daily check-ins</span>
          <span className="text-[10px] text-zinc-700 font-mono">today</span>
        </div>
        {fields.length === 0 ? (
          <div className="text-[12px] text-zinc-600 leading-relaxed">
            No check-ins configured yet. {user.role === "admin" && "Add daily check-in fields in Client settings to start tracking."}
          </div>
        ) : (
          <div className="space-y-2.5">
            {fields.map((f) => {
              const submitted = customSubmitted[f.id];
              return (
                <CustomCheckInField key={f.id} field={f} submitted={submitted}
                  pending={customAnswers[f.id]}
                  setPending={(v) => setCustomAnswers({ ...customAnswers, [f.id]: v })}
                  onSubmit={async (value) => {
                    setBusy(true);
                    try {
                      await api.post(`/clients/${clientId}/checkins`, { type: "custom", field_id: f.id, value, note: f.label });
                      await load();
                      setCustomAnswers((s) => { const n = { ...s }; delete n[f.id]; return n; });
                      toast.success("Recorded");
                      // Notify any listeners (TicketSalesBlock, MasterDashboard, etc.) that today's check-ins changed.
                      window.dispatchEvent(new CustomEvent("tt-checkin-updated", { detail: { clientId, fieldId: f.id, fieldType: f.type, value } }));
                    } catch { toast.error("Failed"); }
                    finally { setBusy(false); }
                  }} />
              );
            })}
          </div>
        )}
      </div>
    </aside>
  );
}

function CustomCheckInField({ field, submitted, pending, setPending, onSubmit }) {
  const done = !!submitted;
  if (done) {
    return (
      <div className="flex items-center gap-2.5 px-3 py-2 rounded-md border border-zinc-800 bg-zinc-900/60" data-testid={`custom-done-${field.id}`}>
        <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center"><Check className="w-3 h-3 text-black" /></div>
        <div className="flex-1 min-w-0">
          <div className="text-[12px] text-zinc-100 truncate">{field.label}</div>
          <div className="text-[10px] text-zinc-500 font-mono truncate">
            {String(submitted.value)} · {submitted.user_name} · {new Date(submitted.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </div>
        </div>
      </div>
    );
  }
  if (field.type === "checkbox") {
    return (
      <button onClick={() => onSubmit(true)} data-testid={`custom-tick-${field.id}`}
        className="w-full flex items-center gap-3 px-3 py-2 rounded-md border border-dashed border-zinc-700 hover:border-zinc-500 hover:bg-zinc-900/40 transition-all group">
        <div className="w-6 h-6 rounded-full border border-zinc-700 group-hover:border-white flex items-center justify-center" />
        <span className="text-[12px] text-zinc-300 group-hover:text-white truncate">{field.label}</span>
      </button>
    );
  }
  const isTicket = field.type === "ticket_sales";
  const isRoas = field.type === "roas";
  const inputType = (field.type === "number" || isTicket || isRoas) ? "number" : "text";
  const prefix = isTicket ? "🎟" : isRoas ? "📈" : null;
  const subLabel = isTicket ? " (yesterday)" : isRoas ? " (today)" : "";
  const placeholder = isTicket
    ? "How many tickets sold yesterday?"
    : isRoas ? "ROAS today (e.g. 2.4)"
    : "Type and press Enter";
  return (
    <div className="space-y-1.5" data-testid={`custom-input-${field.id}`}>
      <label className="text-[11px] text-zinc-400 block">
        {prefix && <span className="mr-1">{prefix}</span>}{field.label}{subLabel}
      </label>
      <div className="flex gap-1.5">
        <input type={inputType} step={isRoas ? "0.01" : undefined} value={pending ?? ""} onChange={(e) => setPending(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (pending ?? "").toString().trim()) onSubmit(pending); }}
          className="flex-1 bg-[#0a0a0a] border border-zinc-800 rounded px-2.5 py-1.5 text-[12px] outline-none focus:border-zinc-500"
          placeholder={placeholder} />
        <button onClick={() => (pending ?? "").toString().trim() && onSubmit(pending)}
          className="px-2.5 rounded bg-white text-black text-[11px] hover:bg-zinc-200"
          data-testid={`custom-submit-${field.id}`}>Save</button>
      </div>
    </div>
  );
}
